import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

// Initialize the modules for solidgauge chart type
const initHighchartsMore = highchartsMore as unknown as (hc: typeof Highcharts) => void;
const initSolidGauge = solidGauge as unknown as (hc: typeof Highcharts) => void;
if (typeof initHighchartsMore === 'function') {
    initHighchartsMore(Highcharts);
}
if (typeof initSolidGauge === 'function') {
    initSolidGauge(Highcharts);
}
import ChartPlaceholder from './ChartPlaceholder';

interface RiskMetrics {
    readonly defaultRatePercent: number;
    readonly avgDaysToDefault: number;
    readonly atRiskContractsCount: number;
    readonly atRiskValueRon: number;
    readonly riskTrend: string;
    readonly portfolioHealthScore: number;
}

interface RiskGaugeChartProps {
    readonly data: RiskMetrics | null;
    readonly loading?: boolean;
}

function getTrendBackground(trend: string): string {
    if (trend === 'improving') return '#f6ffed';
    if (trend === 'worsening') return '#fff2e8';
    return '#fafafa';
}

function getTrendColor(trend: string): string {
    if (trend === 'improving') return '#52c41a';
    if (trend === 'worsening') return '#fa541c';
    return '#8c8c8c';
}

function RiskGaugeChart({ data, loading = false }: RiskGaugeChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || !data) {
        return <ChartPlaceholder loading={loading} hasData={!!data} />;
    }

    // Pre-defined safe color values
    const HEALTH_COLORS = {
        excellent: '#52c41a',
        good: '#73d13d',
        average: '#faad14',
        concerning: '#ff7a45',
        poor: '#ff4d4f',
    } as const;

    // Determine gauge color based on health score - returns only pre-validated hex colors
    const getHealthColor = (score: number): string => {
        if (score >= 80) return HEALTH_COLORS.excellent;
        if (score >= 60) return HEALTH_COLORS.good;
        if (score >= 40) return HEALTH_COLORS.average;
        if (score >= 20) return HEALTH_COLORS.concerning;
        return HEALTH_COLORS.poor;
    };

    const healthColor = getHealthColor(data.portfolioHealthScore);

    const options: Highcharts.Options = {
        chart: {
            type: 'solidgauge',
            height: 280,
        },
        title: {
            text: undefined,
        },
        pane: {
            center: ['50%', '75%'],
            size: '100%',
            startAngle: -90,
            endAngle: 90,
            background: [
                {
                    backgroundColor: '#EEE',
                    innerRadius: '60%',
                    outerRadius: '100%',
                    shape: 'arc',
                    borderWidth: 0,
                },
            ],
        },
        yAxis: {
            min: 0,
            max: 100,
            stops: [
                [0.2, '#ff4d4f'], // Red
                [0.4, '#ff7a45'], // Orange
                [0.6, '#faad14'], // Yellow
                [0.8, '#73d13d'], // Light green
                [1, '#52c41a'], // Green
            ],
            lineWidth: 0,
            tickWidth: 0,
            minorTickInterval: undefined,
            tickAmount: 2,
            title: {
                y: -70,
                text: t('statistics.advanced.portfolioHealth', 'Portfolio Health'),
            },
            labels: {
                y: 16,
            },
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    y: 5,
                    borderWidth: 0,
                    useHTML: true,
                },
            },
        },
        tooltip: {
            enabled: false,
        },
        series: [
            {
                type: 'solidgauge',
                name: t('statistics.advanced.healthScore', 'Health Score'),
                data: [data.portfolioHealthScore],
                dataLabels: {
                    format: `<div style="text-align:center">
                    <span style="font-size:24px;color:${healthColor}">{y}</span>
                    <br/>
                    <span style="font-size:12px;opacity:0.6">/100</span>
                </div>`,
                },
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return (
        <div>
            <HighchartsReact highcharts={Highcharts} options={options} />
            <div style={{ textAlign: 'center', marginTop: '-20px' }}>
                <div
                    style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        backgroundColor: getTrendBackground(data.riskTrend),
                        color: getTrendColor(data.riskTrend),
                        fontSize: '12px',
                        fontWeight: 500,
                    }}
                >
                    {t(`statistics.advanced.trend.${data.riskTrend}`, data.riskTrend)}
                </div>
            </div>
        </div>
    );
}

export default RiskGaugeChart;
